import React, { useRef, useEffect } from 'react';

interface FollowUpChatInputProps {
  text: string;
  setText: (text: string) => void;
  isLoading: boolean;
  onSend: () => void;
  suggestions: string[];
  onSuggestionClick: (suggestion: string) => void;
}

const FollowUpChatInput: React.FC<FollowUpChatInputProps> = ({ text, setText, isLoading, onSend, suggestions, onSuggestionClick }) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (text === '' && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [text]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    // Auto-resize
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 200)}px`;
  };

  return (
    <div className='flex gap-12 w-full items-start'>
      <div className='flex flex-col bg-neutral-10 border border-neutral-20 rounded-16 flex-1'>
        <div
          className={`relative bg-neutral-5 rounded-16 border-b border-neutral-20 transition-all flex flex-col shadow-sm ${
            isLoading ? 'opacity-70 cursor-not-allowed' : 'focus-within:ring-1 focus-within:ring-primary/20 focus-within:border-primary'
          }`}
        >
          <textarea
            ref={textareaRef}
            value={text}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            placeholder='Ask anything to Freya'
            className='w-full bg-transparent border-none outline-none resize-none px-20 pt-20 pb-60 text-neutral-90 placeholder-neutral-50 text-14 disabled:cursor-not-allowed min-h-[120px]'
          />

          <div className='absolute bottom-12 left-12 flex items-center gap-12'>
            <button
              type='button'
              className='w-32 h-32 rounded-full border border-neutral-20 flex items-center justify-center text-neutral-50 hover:bg-neutral-5 hover:text-neutral-80 transition-colors'
              aria-label='Attach file'
            >
              <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                <path d='M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48' />
              </svg>
            </button>
            <button
              type='button'
              className='flex items-center gap-8 h-32 px-12 rounded-8 border border-neutral-20 text-neutral-90 text-14 font-medium hover:bg-neutral-5 transition-colors'
            >
              <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                <rect x='3' y='3' width='18' height='18' rx='2' ry='2' />
                <line x1='3' y1='9' x2='21' y2='9' />
                <line x1='9' y1='21' x2='9' y2='9' />
              </svg>
              Library
            </button>
          </div>

          <div className='absolute bottom-12 right-12'>
            <button
              type='button'
              onClick={onSend}
              disabled={!text.trim() || isLoading}
              className='w-32 h-32 rounded-full bg-primary text-white flex items-center justify-center hover:bg-green-60 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md transform active:scale-95 duration-200'
              aria-label='Send message'
            >
              <svg width='18' height='18' viewBox='0 0 24 24' fill='currentColor' xmlns='http://www.w3.org/2000/svg'>
                <path d='M14.5 4.5L15.65 8.15L19.3 9.3L15.65 10.45L14.5 14.1L13.35 10.45L9.7 9.3L13.35 8.15L14.5 4.5Z' />
                <path d='M6.5 13.5L7.25 15.9L9.65 16.65L7.25 17.4L6.5 19.8L5.75 17.4L3.35 16.65L5.75 15.9L6.5 13.5Z' />
              </svg>
            </button>
          </div>
        </div>

        {suggestions.length > 0 && (
          <div className='bg-neutral-10 p-8 rounded-16 flex flex-wrap gap-8'>
            {suggestions.map((suggestion) => (
              <button
                key={suggestion}
                type='button'
                onClick={() => onSuggestionClick(suggestion)}
                className='px-16 py-8 bg-white border border-neutral-20 rounded-full text-14 text-neutral-60 hover:bg-white hover:text-neutral-90 hover:border-neutral-40 transition-colors shadow-sm'
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Vertical Navigation Buttons */}
      <div className='flex flex-col gap-4 bg-white p-4 rounded-8 border border-neutral-20 shadow-sm'>
        <button
          type='button'
          className='w-32 h-32 flex items-center justify-center text-neutral-50 hover:text-primary hover:bg-neutral-5 rounded-4 transition-colors'
          aria-label='Scroll to top'
        >
          <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
            <path d='M17 11l-5-5-5 5M17 18l-5-5-5 5' />
          </svg>
        </button>
        <button
          type='button'
          className='w-32 h-32 flex items-center justify-center text-neutral-50 hover:text-primary hover:bg-neutral-5 rounded-4 transition-colors'
          aria-label='Scroll up'
        >
          <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
            <path d='M18 15l-6-6-6 6' />
          </svg>
        </button>
        <button
          type='button'
          className='w-32 h-32 flex items-center justify-center text-neutral-50 hover:text-primary hover:bg-neutral-5 rounded-4 transition-colors'
          aria-label='Scroll down'
        >
          <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
            <path d='M6 9l6 6 6-6' />
          </svg>
        </button>
        <button
          type='button'
          className='w-32 h-32 flex items-center justify-center text-neutral-50 hover:text-primary hover:bg-neutral-5 rounded-4 transition-colors'
          aria-label='Scroll to bottom'
        >
          <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
            <path d='M7 13l5 5 5-5M7 6l5 5 5-5' />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default FollowUpChatInput;
